import React, { useEffect, useState, useMemo } from "react";
import { Row, Col } from "reactstrap";
import { useGetPortfolios } from "api/portfolios/service";
import { useTranslation } from "react-i18next";
import { useGetChannels } from "api/channels/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import Select from "components/Select";

const BookingInfo = React.forwardRef(({ isTheme2 = false, ...props }, ref) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(props.activeColor), [props.activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
  const { data: portfolios, isLoading: portfoliosLoading } = useGetPortfolios();
  const { data: sources } = useGetChannels("source");
  const { data: lenders, isLoading: lendersLoading } = useGetChannels("finance_company_bank");
  const { data: locationsData } = useGetChannels("sales_location_branch");

  const [accountDescription, setAccountDescription] = useState("");
  const [loanNumber, setLoanNumber] = useState(null);
  const [portfolioId, setPortfolioId] = useState(null);
  const [servicingBranch, setServicingBranch] = useState(null);
  const [lenderId, setLenderId] = useState(null);
  const [source, setSource] = useState(null);
  const [showErrors, setShowErrors] = useState(false);


  // Notify parent component of form changes
  useEffect(() => {
    if (props.onFormChange) {
      props.onFormChange({
        accountDescription,
        loanNumber,
        portfolioId,
        servicingBranch,
        lenderId,
        source,
      });
    }
  }, [accountDescription, loanNumber, portfolioId, servicingBranch, lenderId, source]);
  // Prefill from parent when provided
  React.useEffect(() => {
    const iv = props.initialValues || {};
    setAccountDescription(iv.accountDescription ?? "");
    setLoanNumber(iv.act_number ?? "");
    setPortfolioId(iv.portfolioId);
    setServicingBranch(iv.servicingBranch?.id?.toString() || null);
    setLenderId(iv.lenderId);
    setSource(iv.source);
  }, [props.prefillKey]);

  const createdRef = React.useRef(false);

  React.useImperativeHandle(ref, () => ({
    state: {
      accountDescription,
      loanNumber,
      portfolioId,
      servicingBranch,
      lenderId,
      source,
    },
    isValidated: async () => {
      if (props.skipValidation) return true;
      const isValid = loanNumber !== null && portfolioId !== null;
      if (!isValid) {
        setShowErrors(true);
        return false;
      }
      if (!createdRef.current && typeof props.onCreateCalculus === 'function') {
        const payload = {
          accountDescription,
          loanNumber,
          portfolioId,
          servicingBranch,
          lenderId,
          source,
        };
        const ok = await props.onCreateCalculus(payload);
        if (!ok) {
          return false;
        }
        createdRef.current = true;
      }
      return true;
    },
  }));

  return (
    <div className="card">
      <div className="card-body">
        <Row >
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.accountDescription')}</label>
              <input
                type="text"
                className="form-control"
                style={inputStyle}
                placeholder={t('addAccount.enterAccountDescription')}
                value={accountDescription}
                onChange={(e) => setAccountDescription(e.target.value)}
              />
            </div>
          </Col>
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.loanNumber')} <span style={{ color: 'red' }}>*</span></label>
              <input
                type="text"
                className={`form-control${showErrors && loanNumber.trim() === "" ? " is-invalid" : ""}`}
                style={inputStyle}
                placeholder={t('addAccount.enterLoanNumber')}
                value={loanNumber}
                onChange={(e) => setLoanNumber(e.target.value)}
              />

            </div>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.portfolio')} <span style={{ color: 'red' }}>*</span></label>
              <Select
                activeColor={props.activeColor}
                styles={selectStyles}
                className={`react-select${showErrors && portfolioId === null ? " is-invalid" : ""}`}
                placeholder={t('addAccount.selectPortfolio')}
                name="portfolio"
                isSearchable
                closeMenuOnSelect={true}
                value={portfolioId ? { value: portfolioId, label: portfolios?.results?.find((p) => p.id === portfolioId)?.code ? `${portfolios?.results?.find((p) => p.id === portfolioId)?.code} - ${portfolios?.results?.find((p) => p.id === portfolioId)?.name}` : portfolios?.results?.find((p) => p.id === portfolioId)?.name } : null}
                onChange={(value) => setPortfolioId(value?.value || null)}
                options={portfolios?.results?.map((p) => ({ value: p.id, label: p.code ? `${p.code} - ${p.name}` : p.name }))}
                isDisabled={portfoliosLoading}
              />
            </div>
          </Col>
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.servicingBranch')}</label>
              <Select
                activeColor={props.activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('addAccount.selectServicingBranch')}
                name="servicingBranch"
                isSearchable
                isClearable
                closeMenuOnSelect={true}
                value={servicingBranch ? {
                  value: servicingBranch,
                  label: locationsData?.results?.find(loc => String(loc.id) === String(servicingBranch))?.name || servicingBranch
                } : null}
                onChange={(value) => setServicingBranch(value?.value || "")}
                options={[
                  ...(locationsData?.results?.map((location) => ({
                    value: String(location.id),
                    label: location.name
                  })) || [])
                ]}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.lender')}</label>
              <Select
                activeColor={props.activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('addAccount.selectLender')}
                name="lender"
                isSearchable
                closeMenuOnSelect={true}
                value={lenderId ? { value: lenderId, label: lenders?.results?.find((l) => l.id === lenderId)?.name } : null}
                onChange={(value) => setLenderId(value?.value || "")}
                options={lenders?.results?.map((l) => ({ value: l.id, label: l.name }))}
                isDisabled={lendersLoading}
                isClearable
              />
            </div>
          </Col>
          <Col md="6">
            <div className="form-group">
              <label>{t('addAccount.source')}</label>
              <Select
                activeColor={props.activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('addAccount.selectSource')}
                name="source"
                isSearchable
                closeMenuOnSelect={true}
                value={source ? { value: source, label: sources?.results?.find((s) => s.id === source)?.name } : null}
                onChange={(value) => setSource(value?.value || "")}
                options={sources?.results?.map((s) => ({ value: s.id, label: s.name }))}
                isClearable
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
});

export default BookingInfo;


